import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Bell, Check, CheckCheck } from 'lucide-react';
import { Button } from '../ui/button';

export const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data.data.notifications);
      setUnreadCount(response.data.data.unread_count);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllRead = async () => {
    setLoading(true);
    try {
      await api.patch('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNotificationClick = (notif) => {
    if (!notif.read) markAsRead(notif.notification_id);
    if (notif.link) navigate(notif.link);
    setOpen(false);
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'success': return 'bg-emerald-500';
      case 'error': return 'bg-red-500';
      case 'warning': return 'bg-amber-500';
      default: return 'bg-indigo-500';
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
        data-testid="notification-bell-btn"
      >
        <Bell className="h-5 w-5 text-slate-500" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 h-5 w-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-sm" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-12 w-80 rounded-xl shadow-xl border overflow-hidden z-50"
          style={{ background: '#ffffff', borderColor: '#e2e8f0' }}
          data-testid="notification-dropdown"
        >
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid #f1f5f9' }}>
            <h3 className="font-bold text-sm" style={{ color: '#1e293b' }}>Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-teal-600 h-7 hover:bg-teal-50"
                onClick={markAllRead}
                disabled={loading}
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-80 overflow-y-auto" data-testid="notification-list">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-sm" style={{ color: '#94a3b8' }} data-testid="no-notifications">
                No notifications yet
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  onClick={() => handleNotificationClick(notif)}
                  className="px-4 py-3 cursor-pointer transition-colors"
                  style={{
                    borderBottom: '1px solid #f8fafc',
                    background: !notif.read ? '#f0fdfa' : '#ffffff',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = !notif.read ? '#e6fffa' : '#f8fafc'}
                  onMouseLeave={(e) => e.currentTarget.style.background = !notif.read ? '#f0fdfa' : '#ffffff'}
                  data-testid={`notification-item-${notif.notification_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`h-2 w-2 rounded-full mt-2 flex-shrink-0 ${getTypeColor(notif.type)}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm" style={{ color: !notif.read ? '#0f172a' : '#475569', fontWeight: !notif.read ? 600 : 400 }}>
                        {notif.title}
                      </p>
                      <p className="text-xs mt-0.5 line-clamp-2" style={{ color: '#64748b' }}>{notif.message}</p>
                      <p className="text-xs mt-1" style={{ color: '#94a3b8' }}>{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.read && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markAsRead(notif.notification_id); }}
                        className="p-1 rounded hover:bg-slate-100 flex-shrink-0"
                        title="Mark as read"
                      >
                        <Check className="h-3 w-3" style={{ color: '#94a3b8' }} />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
